<script setup lang="ts">
import VulnerabilitySeverity from '@/_core/entities/vulnerability/ui/VulnerabilitySeverity/VulnerabilitySeverity.vue';
import type { TComplianceMappingRequirement, TComplianceMappingItem } from '@/_core/entities/report/models/types';

defineProps<{
  requirement: TComplianceMappingRequirement;
  expanded: boolean;
}>();

defineEmits<{
  (e: 'toggle'): void;
}>();

const ROLE_ORDER = ['primary', 'secondary', 'universal'];

const ROLE_CONFIG: Record<string, { label: string; severityKey: string; badgeClass: string; tooltip: string }> = {
  primary: {
    label: 'Primary',
    severityKey: 'primary',
    badgeClass: 'tw:bg-[#FDC7C0] tw:border-[#FB7462]/40 tw:text-[#FB7462]',
    tooltip: 'This item directly reflects the main cause of the vulnerability',
  },
  secondary: {
    label: 'Secondary',
    severityKey: 'secondary',
    badgeClass: 'tw:bg-[#FFE1C6] tw:border-[#FF7A08]/40 tw:text-[#FF7A08]',
    tooltip: 'This item adds broader context or supporting controls related to the vulnerability',
  },
  universal: {
    label: 'Supporting',
    severityKey: 'supporting',
    badgeClass: 'tw:bg-[#DFEBFF] tw:border-[#3180F6]/40 tw:text-[#3180F6]',
    tooltip: 'This item is relevant to the vulnerability, but less directly',
  },
};

function sortedRoles(roles: string[]) {
  return [...roles].sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b));
}

function cfg(role: string) {
  return ROLE_CONFIG[role] || ROLE_CONFIG.universal;
}

const ROW_GRID = 'tw:grid-cols-[1.5rem_minmax(10rem,1fr)_minmax(16rem,24rem)] tw:max-[500px]:grid-cols-[1fr]';

const TREE_LINE =
  'tw:relative tw:max-[500px]:before:hidden tw:max-[500px]:after:hidden ' +
  "tw:before:content-[''] tw:before:absolute tw:before:left-[36px] tw:before:top-0 tw:before:bottom-0 tw:before:w-px tw:before:bg-[#e4e8ee] " +
  "tw:after:content-[''] tw:after:absolute tw:after:left-[36px] tw:after:top-1/2 tw:after:w-4 tw:after:h-px tw:after:bg-[#e4e8ee]";

const TREE_LINE_LAST =
  'tw:before:!bottom-1/2 tw:before:!w-4 tw:before:!bg-transparent tw:before:border-l tw:before:border-b tw:before:border-[#e4e8ee] tw:before:rounded-bl-[10px] tw:after:hidden';
</script>

<template>
  <div class="tw:border-t tw:border-[#E4E8EE] tw:first:border-t-0">
    <!-- Requirement header -->
    <div
      :class="[
        'tw:grid tw:items-center tw:gap-6 tw:p-6 tw:max-[399px]:px-0 tw:typography-small tw:cursor-pointer tw:hover:bg-black/2 tw:transition-colors tw:duration-75',
        ROW_GRID,
      ]"
      @click="$emit('toggle')"
    >
      <button
        type="button"
        class="tw:size-6 tw:grid tw:place-items-center tw:rounded tw:max-[500px]:hidden"
        @click.stop="$emit('toggle')"
      >
        <span
          class="tw:inline-block tw:transition-transform tw:text-[#949DB8]"
          :class="{ 'tw:rotate-90': expanded }"
        >▸</span>
      </button>

      <div class="tw:min-w-0 tw:flex tw:items-center tw:gap-2">
        <button
          type="button"
          class="tw:hidden tw:max-[500px]:inline-block tw:text-[#949DB8] tw:transition-transform"
          :class="{ 'tw:rotate-90': expanded }"
          @click.stop="$emit('toggle')"
        >▸</button>
        <span class="tw:truncate tw:font-medium tw:text-[#021631]">{{ requirement.name }}</span>
        <a
          v-if="requirement.externalUrl"
          :href="requirement.externalUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="tw:text-[#5C6A7A] tw:typography-small tw:shrink-0 tw:font-bold tw:no-underline tw:hover:underline"
          @click.stop
        >↗</a>
      </div>

      <div class="tw:flex tw:items-center tw:gap-3 tw:flex-wrap">
        <VulnerabilitySeverity
          v-for="role in sortedRoles(requirement.relevance)"
          :key="role"
          :severity="cfg(role).severityKey"
        />
      </div>
    </div>

    <!-- Expanded items -->
    <div v-if="expanded" class="tw:flex tw:flex-col tw:typography-small tw:pb-2">
      <div
        v-for="(item, idx) in requirement.items"
        :key="item.id"
      >
        <div
          :class="[
            'tw:grid tw:items-center tw:gap-6 tw:p-6 tw:max-[399px]:px-0',
            ROW_GRID,
            TREE_LINE,
            idx === requirement.items.length - 1 ? TREE_LINE_LAST : '',
          ]"
        >
          <div class="tw:max-[500px]:hidden"></div>

          <div class="tw:min-w-0 tw:flex tw:items-center tw:gap-3">
            <a
              v-if="item.link"
              :href="item.link"
              target="_blank"
              rel="noopener noreferrer"
              class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-1 tw:rounded-full tw:bg-[#F5F7FA] tw:text-[#5C6A7A] tw:text-xs tw:no-underline tw:hover:underline"
              @click.stop
            >
              <span class="tw:truncate">{{ item.itemId }}</span>
              <span class="tw:font-bold tw:shrink-0">↗</span>
            </a>
            <span
              v-else
              class="tw:inline-flex tw:items-center tw:gap-1 tw:px-3 tw:py-1 tw:rounded-full tw:bg-[#F5F7FA] tw:text-[#5C6A7A] tw:text-xs"
            >
              <span class="tw:truncate">{{ item.itemId }}</span>
            </span>
          </div>

          <div>
            <span
              class="tw:inline-flex tw:items-center tw:justify-center tw:min-w-6 tw:px-2.5 tw:py-0.5 tw:rounded-full tw:border tw:text-xs tw:font-medium"
              :class="cfg(item.role).badgeClass"
              :title="cfg(item.role).tooltip"
            >{{ cfg(item.role).label }}</span>
          </div>
        </div>

        <div
          v-if="idx !== requirement.items.length - 1"
          class="tw:h-px tw:bg-[#E4E8EE] tw:ml-14 tw:max-[500px]:ml-0"
        />
      </div>
    </div>
  </div>
</template>
